import { OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2 } from './offer'
import { INTL_DAY_MONTH_NAME_SHORT } from './dateFormats'
import { OfferContextMap } from 'analytics/snowplow/context'
import { defineMessages, type IntlShape, type MessageDescriptor } from 'react-intl'
import { formatCurrency } from 'lib/format/formatCurrencyIntl'
import Places from 'constants/places'
import { LUXURY_ESCAPES } from 'constants/brands'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { intlDateFormatter } from 'lib/datetime/dateUtils'

export const TOUR_CHILD_MAX_AGE = 15
export const TOUR_V2_OFFER_DUE_DATE_DAY_THRESHOLD = 105

export const DIRECT_TOUR = 'direct_tour'
export const PARTNER_TOUR = 'partner_tour'
export const CONNECTION_TOUR = 'connection_tour'
export const CRUISE_TOUR = 'cruise_tour'

export const ULTRA_LUX_TOUR = 'ultra_lux_tour'

export const TOUR_ARTEFACT_SIZE_MAX_BYTES = 5 * 1024 * 1024
export const TOUR_ARTEFACT_UPLOAD_DEADLINE_DAYS_BEFORE_DEPARTURE = 7
export const TOUR_ARTEFACT_RETENTION_DAYS_AFTER_DEPARTURE = 7

/** Customer self-service edits to submitted tour traveller details are allowed until this many hours before departure (instant on stored departure timestamp). */
export const TOUR_TRAVELLER_SELF_EDIT_MIN_HOURS_BEFORE_DEPARTURE = 72

export const TOUR_V2_OFFER_LIST_TYPES: Array<App.Tours.V2ParentOfferType> = [OFFER_TYPE_TOUR_V2]
export const TOUR_V1_OFFER_LIST_TYPES: Array<App.OfferType> = [OFFER_TYPE_TOUR]
export const TOUR_V1_OFFER_LIST_FILTER_TYPES: Array<App.OfferType> = [OFFER_TYPE_TOUR]
export const TOURS_OFFER_LIST_EXCLUDED_PARTNERSHIP_CODES = new Set([
  'qantas',
  'velocity',
])

export const TOUR_ORDER_TRAVELLER_DETAILS_TTL: number = 300 // in seconds

export enum TourV2ContentSections {
  GALLERY = 'gallery',
  OVERVIEW = 'overview',
  ITINERARY = 'itinerary',
  VARIATIONS = 'variations',
  PREFERRED_PACKAGES = 'preferred-packages',
  INCLUSIONS = 'inclusions',
  DEPARTURES = 'dates-and-pricing',
  DEPARTURE_DATES = 'departure_dates',
  FINE_PRINT = 'things-to-know',
  FAQ = 'frequently-asked-questions',
  YOU_MAY_ALSO_LIKE = 'you-may-also-like',
  REVIEWS = 'reviews',
  ADDITIONAL_WAYS = 'additional-ways',
  SIMILAR_TOURS = 'similar-tours',
}

export const TOUR_V2_STICKY_HEADER_ENABLED_SECTIONS = new Set<TourV2ContentSections>([
  TourV2ContentSections.OVERVIEW,
  TourV2ContentSections.ITINERARY,
  TourV2ContentSections.VARIATIONS,
  TourV2ContentSections.PREFERRED_PACKAGES,
  TourV2ContentSections.INCLUSIONS,
  TourV2ContentSections.DEPARTURES,
  TourV2ContentSections.FINE_PRINT,
  TourV2ContentSections.FAQ,
  TourV2ContentSections.REVIEWS,
])

export const TOUR_V2_TARGET_OCCUPANT_TYPES: Set<App.OccupantType> = new Set(['adults', 'children'])

export const TOUR_V2_SEARCH_TYPES: Array<App.SearchType> = ['destination', 'tour']

export const TOUR_V2_SEARCH_HERO_IMAGE_ID = 'adkvxdgnii1kvb4s970c'
export const TOUR_V2_CATALOGUE_HERO_IMAGE_ID = 'tour-catalog-header'
export const TOUR_V2_SEARCH_HERO_IMAGE_ASIA_CAMPAIGN = 'tour-hero-asia-campaign-background-v2'
export const TOUR_V2_HERO_IMAGE_ASIA_MOBILE_BACKGROUND = 'tour-hero-asia-campaign-mobile-background-v2'
export const TOUR_V2_PROFILE_ASIA_CAMPAIGN = 'profile-asia-campaign'
export const TOUR_ASIA_CAMPAIGN = 'TOURASIA'
export const TOUR_PROMO_CODE = 'TOURSALE'
export const TOUR_PROMO_END_DATE = '2025-01-31T00:00:00Z'

const ARCTIC_CHARTER_DEPOSIT_AMOUNT = formatCurrency(1000, 'AUD', 'AU', { format: 'roundedDollar' })

const tourCampaignMessages = defineMessages({
  name: {
    id: '3WSXSWEkbj9zUSSHrpjT',
    defaultMessage: 'Tours Campaign',
    description: 'Generic campaign title for reusable tours promotional surfaces',
  },
  campaignLabel: {
    id: '9zVPsE8jGSOKptWBfswz',
    defaultMessage: 'Special Offer',
    description: 'Eyebrow label for reusable tours campaign banner and promotional surfaces',
  },
  message: {
    id: 'MzQ-1D208OJiBvF_W2D4',
    defaultMessage: 'Limited-time offer on select tours.',
    description: 'Generic promotional message for reusable tours campaign surfaces',
  },
  tag: {
    id: 'jrqUzZsVLwfv10UP5xgH',
    defaultMessage: 'Special Offer',
    description: 'Generic promotional pill label for offers included in the reusable tours campaign',
  },
  cta: {
    id: 'kpg2YC_YH9r7c_7BN0dK',
    defaultMessage: 'Limited-time offer available on this tour.',
    description: 'Generic call to action copy for the reusable tours campaign on the tour offer page',
  },
  termsAndConditionsOffer: {
    id: 'Dpiicqo5-V4u3WjDVRHC',
    defaultMessage: '*Terms and conditions apply to this limited-time tour offer.*',
    description: 'Generic offer terms paragraph shown in the reusable tours campaign terms modal on tour offer pages',
  },
  termsAndConditionsPricing: {
    id: 'ShpGTfxlxVkF7ieGBHsL',
    defaultMessage: '**Important:** Our prices are dynamic to offer fantastic rates for our members; however, this means they may fluctuate. The prices are subject to change and cannot be guaranteed.',
    description: 'Dynamic pricing terms paragraph shown in the reusable tours campaign terms modal on tour offer pages',
  },
  tierName: {
    id: 'SjHnekdZ-8ZU5-DacFR0',
    defaultMessage: 'Primary campaign offer',
    description: 'Internal-facing tier name for the reusable tours campaign offer treatment',
  },
})

const tourTravelTagLabelMessages = defineMessages({
  internationalFlightsIncluded: {
    id: 'mD8RfLyRh0KWq4B848HC',
    defaultMessage: 'International Flights Included',
    description: 'Travel tag label for the Arctic Charter tour indicating international flights are included',
  },
  arcticCharterDeposit: {
    id: 'gihpQfpwlYj9ucgPyTUZ',
    defaultMessage: 'Secure with {amount} deposit',
    description: 'Travel tag label for the Arctic Charter tour indicating the deposit amount',
  },
})

export const TOUR_CAMPAIGN_CONFIG = {
  tag: 'TOURS_CAMPAIGN_DISABLED',
  brands: [LUXURY_ESCAPES],
  countdownStartDate: '2026-05-19T00:00:00+10:00',
  endDate: '2026-05-31T23:59:59+10:00',
  regionCode: 'AU',
  searchFilterTag: 'TOURS_CAMPAIGN_DISABLED',
  tiers: [
    {
      id: 'primary',
      tourIds: [],
    },
  ],
} satisfies Omit<App.Tours.TourCampaign, 'name' | 'campaignLabel' | 'message' | 'tiers'> & {
  tiers: Array<Omit<App.Tours.TourCampaignTier, 'name' | 'tag' | 'cta' | 'termsAndConditions'>>
}

export function getTourCampaignConfig(intl: IntlShape = getCurrentIntl()): App.Tours.TourCampaign {
  const termsAndConditions = [
    intl.formatMessage(tourCampaignMessages.termsAndConditionsOffer),
    intl.formatMessage(tourCampaignMessages.termsAndConditionsPricing),
  ].join('\n\n')

  return {
    ...TOUR_CAMPAIGN_CONFIG,
    name: intl.formatMessage(tourCampaignMessages.name),
    campaignLabel: intl.formatMessage(tourCampaignMessages.campaignLabel),
    message: intl.formatMessage(tourCampaignMessages.message),
    tiers: TOUR_CAMPAIGN_CONFIG.tiers.map((tier) => ({
      ...tier,
      name: intl.formatMessage(tourCampaignMessages.tierName),
      tag: intl.formatMessage(tourCampaignMessages.tag),
      cta: intl.formatMessage(tourCampaignMessages.cta),
      termsAndConditions,
    })),
  }
}

export type TourSearchSpecialOfferCampaignIntlKey = 'toursCampaign'

export interface TourSearchSpecialOfferCampaignConfig {
  brands?: Array<string>
  endDate?: string
  intlKey: TourSearchSpecialOfferCampaignIntlKey
  regionCodes?: Array<string>
  tag: string
}

export const TOUR_SEARCH_SPECIAL_OFFERS_CAMPAIGNS: Array<TourSearchSpecialOfferCampaignConfig> = [
  {
    tag: TOUR_CAMPAIGN_CONFIG.searchFilterTag ?? '',
    intlKey: 'toursCampaign',
    endDate: TOUR_CAMPAIGN_CONFIG.endDate,
    brands: TOUR_CAMPAIGN_CONFIG.brands,
    regionCodes: TOUR_CAMPAIGN_CONFIG.regionCode ? [TOUR_CAMPAIGN_CONFIG.regionCode] : undefined,
  },
]

export interface TourStyleGroupBenefitVisibilityConfig {
  enabled: boolean
  tourIds: Array<string>
}

/**
 * When enabled, the configured tours will hide the max group size benefit tile
 * from the styled tour offer page key benefits section.
 */
export const TOUR_STYLE_GROUP_BENEFIT_VISIBILITY_CONFIG: TourStyleGroupBenefitVisibilityConfig = {
  enabled: true,
  tourIds: [
    'tour-1c41669f-b341-4fca-9f97-79b44b7b1df9', // Staging
    'tour-3cf2944d-b311-41f6-a90c-5cebf616fabf',
    'tour-63ec2a75-fd6c-4f43-8b24-691353bd96ce',
  ],
}

export function shouldDisplayTourStylesGroupBenefit(
  tourId: string,
  config: TourStyleGroupBenefitVisibilityConfig = TOUR_STYLE_GROUP_BENEFIT_VISIBILITY_CONFIG,
): boolean {
  return !config.enabled || !config.tourIds.includes(tourId)
}

export function getTourPromoCodeUrgencyMessage(): string {
  const intl = getCurrentIntl()

  return intl.formatMessage(
    {
      id: 'MnmNqXOsuRsKEO41ggSz',
      defaultMessage: 'Code: {promoCode} ends {endDate}',
      description: 'Urgency message shown on tours offers with the promo code and its ending date',
    },
    {
      promoCode: TOUR_PROMO_CODE,
      endDate: intlDateFormatter(
        TOUR_PROMO_END_DATE,
        INTL_DAY_MONTH_NAME_SHORT,
        intl.locale,
        'UTC',
      ),
    },
  )
}

export function isTourCampaignTaggedOffer(campaignTags?: Array<string>): boolean {
  return Boolean(campaignTags?.includes(TOUR_CAMPAIGN_CONFIG.tag))
}

export function getTourCampaignTier(
  campaignTags?: Array<string>,
  intl: IntlShape = getCurrentIntl(),
): App.Tours.TourCampaignTier | undefined {
  if (!isTourCampaignTaggedOffer(campaignTags)) {
    return undefined
  }

  return getTourCampaignConfig(intl).tiers[0]
}

interface TourTravelTagLabelConfig {
  label: MessageDescriptor
  values?: Record<string, string>
  showIcon?: boolean
  tourIds: Array<string>
}

const TOUR_TRAVEL_TAG_LABELS_CONFIG: Array<TourTravelTagLabelConfig> = [
  {
    label: tourTravelTagLabelMessages.internationalFlightsIncluded,
    tourIds: [
      'tour-6a4b516e-8398-4bfc-8d76-aaa15a957bd5', // Arctic Charter
    ],
  },
  {
    label: tourTravelTagLabelMessages.arcticCharterDeposit,
    values: {
      amount: ARCTIC_CHARTER_DEPOSIT_AMOUNT,
    },
    showIcon: false,
    tourIds: [
      'tour-6a4b516e-8398-4bfc-8d76-aaa15a957bd5', // Arctic Charter
    ],
  },
]

export interface TourTravelTagLabel {
  label: string
  showIcon: boolean
}

export function getTourTravelTags(
  tourId: string,
  intl: IntlShape = getCurrentIntl(),
): Array<TourTravelTagLabel> {
  return TOUR_TRAVEL_TAG_LABELS_CONFIG
    .filter(({ tourIds }) => tourIds.includes(tourId))
    .map(({ label, values, showIcon = true }) => ({
      label: intl.formatMessage(label, values),
      showIcon,
    }))
}

export function getTourTravelTagLabels(
  tourId: string,
  intl: IntlShape = getCurrentIntl(),
): Array<string> {
  return getTourTravelTags(tourId, intl).map(({ label }) => label)
}

export function isTourSearchSpecialOfferCampaignActive(
  campaign: TourSearchSpecialOfferCampaignConfig,
  regionCode: string,
  brand: string,
): boolean {
  if (campaign.brands?.length && !campaign.brands.includes(brand)) {
    return false
  }

  if (campaign.regionCodes?.length && !campaign.regionCodes.includes(regionCode)) {
    return false
  }

  if (!campaign.endDate) {
    return true
  }

  const campaignEndTime = new Date(campaign.endDate).getTime()

  return !Number.isNaN(campaignEndTime) && Date.now() <= campaignEndTime
}

export function isTourCampaignActive(regionCode?: string, brand?: string): boolean {
  const campaignEndTime = new Date(TOUR_CAMPAIGN_CONFIG.endDate).getTime()
  const isBrandAllowed = !TOUR_CAMPAIGN_CONFIG.brands?.length
    || Boolean(brand && TOUR_CAMPAIGN_CONFIG.brands.includes(brand))

  return isBrandAllowed
    && regionCode === TOUR_CAMPAIGN_CONFIG.regionCode
    && !Number.isNaN(campaignEndTime)
    && Date.now() <= campaignEndTime
}

export const TOUR_V2_LOCATION_SEARCH_TYPEAHEAD_TYPES: Array<App.SearchPlaceType> = [
  'city',
  'colloquial_area',
  'continent',
  'country',
  'high_level_region',
  'multi_city_vicinity',
  'province_state',
]

export const TOUR_MINIMUM_VALUED_AT_PERCENTAGE_TO_SHOW = 5

export const DEFAULT_TOUR_V2_OFFER_MAX_CHILD_AGE = 17

export const DEFAULT_TOUR_V2_PRIVATE_MAX_CHILD_AGE = 15

export const VendorsThatRequireTravellerDetails = new Set(['00128000018BZkIAAW', '0010I00001ba7JdQAI', '0010I00001f6bHvQAI', '0010I000028DrsxQAC', '0012y00000LHHZoAAP'])

export const TOUR_V2_MALE_TRAVELLER_TITLES = ['Mr', 'Master']

export const TOUR_V2_FEMALE_TRAVELLER_TITLES = ['Ms', 'Mrs', 'Miss']

export const TOUR_V2_LE_SOURCE = 'salesforce'

export const TOUR_V2_PACKAGE_OPTION_BASE = 'Base'
export const TOUR_V2_PACKAGE_OPTION_EXTENDED = 'Extended'
export const TOUR_V2_PACKAGE_OPTION_EXTENDED_ALTERNATIVE = 'Extended Alternative'

export const tourOptionPackages = [
  TOUR_V2_PACKAGE_OPTION_BASE,
  TOUR_V2_PACKAGE_OPTION_EXTENDED,
  TOUR_V2_PACKAGE_OPTION_EXTENDED_ALTERNATIVE,
]

export const tourOptionPackagesEvent = {
  [TOUR_V2_PACKAGE_OPTION_BASE]: OfferContextMap.VIEW_TOUR_OPTIONS_PRESSED,
  [TOUR_V2_PACKAGE_OPTION_EXTENDED]: OfferContextMap.VIEW_TOUR_OPTIONS_EXTENDED_PRESSED,
  [TOUR_V2_PACKAGE_OPTION_EXTENDED_ALTERNATIVE]: OfferContextMap.VIEW_TOUR_OPTIONS_EXTENDED_ALT_PRESSED,
}

export enum PACKAGE_UPGRADE_VALUES {
  REGULAR = 'regular',
  VIP = 'vip',
  PLATINUM = 'platinum',
}

export const TourOptionPackageUpgrades = [PACKAGE_UPGRADE_VALUES.REGULAR, PACKAGE_UPGRADE_VALUES.VIP, PACKAGE_UPGRADE_VALUES.PLATINUM]

export const SUPERIOR_PACKAGE_OPTIONS_AVAILABLES: Record<string, Array<PACKAGE_UPGRADE_VALUES>> = {
  [PACKAGE_UPGRADE_VALUES.REGULAR]: TourOptionPackageUpgrades,
  [PACKAGE_UPGRADE_VALUES.VIP]: [PACKAGE_UPGRADE_VALUES.VIP, PACKAGE_UPGRADE_VALUES.PLATINUM],
  [PACKAGE_UPGRADE_VALUES.PLATINUM]: [PACKAGE_UPGRADE_VALUES.PLATINUM],
}

export const ALL_TOUR_PACKAGE_TYPE = 'all'

export const getOfferPackageUpgradeDisplayTextMap = (offerId: string) => {
  const intl = getCurrentIntl()
  const defaultPackageUpgradeDisplayTextMap = new Map([
    [PACKAGE_UPGRADE_VALUES.REGULAR, intl.formatMessage({
      id: 'b9palHdolSvMp-IlZZ2i',
      defaultMessage: 'Standard Package',
      description: 'Default package upgrade label for the regular tour package option',
    })],
    [PACKAGE_UPGRADE_VALUES.VIP, intl.formatMessage({
      id: 'h2_EtFC2HWhSVQilUwNQ',
      defaultMessage: 'Upgraded Package',
      description: 'Default package upgrade label for the VIP tour package option',
    })],
    [PACKAGE_UPGRADE_VALUES.PLATINUM, intl.formatMessage({
      id: 'jzV5qmTIVdVTCeKuqzLS',
      defaultMessage: 'Deluxe Package',
      description: 'Default package upgrade label for the platinum tour package option',
    })],
  ])
  const ecoBusinessPackageUpgradeDisplayTextMap = new Map([
    [PACKAGE_UPGRADE_VALUES.REGULAR, intl.formatMessage({
      id: '4I2qlEMsRpcT93YPf1s2',
      defaultMessage: 'Economy Package',
      description: 'Special package upgrade label for the regular tour package option on economy/business style tours',
    })],
    [PACKAGE_UPGRADE_VALUES.VIP, intl.formatMessage({
      id: 'qXZu8tX8sT1bYL8T4v2l',
      defaultMessage: 'Business Package',
      description: 'Special package upgrade label for the VIP tour package option on economy/business style tours',
    })],
    [PACKAGE_UPGRADE_VALUES.PLATINUM, intl.formatMessage({
      id: 'Ih1cWqlfC1yBu12IQMrl',
      defaultMessage: 'First Class Package',
      description: 'Special package upgrade label for the platinum tour package option on economy/business style tours',
    })],
  ])
  const viewPackageUpgradeDisplayTextMap = new Map([
    [PACKAGE_UPGRADE_VALUES.REGULAR, intl.formatMessage({
      id: 'YiMyIVtwzdPrffMBefv_',
      defaultMessage: 'King or Twin Package',
      description: 'Package upgrade label for the regular tour package option on room-view tour variants',
    })],
    [PACKAGE_UPGRADE_VALUES.VIP, intl.formatMessage({
      id: 'eh62fJvhKPjvq2vGL5fq',
      defaultMessage: 'Ocean View w. Club Access Upgrade',
      description: 'Package upgrade label for the VIP tour package option on room-view tour variants',
    })],
    [PACKAGE_UPGRADE_VALUES.PLATINUM, intl.formatMessage({
      id: 'KuSXMjpOM85v7PM4_QRw',
      defaultMessage: 'Regency Suite w. Club Access Upgrade',
      description: 'Package upgrade label for the platinum tour package option on room-view tour variants',
    })],
  ])
  const rangeViewPackageUpgradeDisplayTextMap = new Map([
    [PACKAGE_UPGRADE_VALUES.REGULAR, intl.formatMessage({
      id: 'nZiKFk8reb-EFci5sdrq',
      defaultMessage: 'Classic to King or Twin Package',
      description: 'Package upgrade label for the regular tour package option on room-range tour variants',
    })],
    [PACKAGE_UPGRADE_VALUES.VIP, intl.formatMessage({
      id: 'xPT7_Lg-DWkegz1fhgO-',
      defaultMessage: 'Superior to Ocean View w. Club Access Upgrade',
      description: 'Package upgrade label for the VIP tour package option on room-range tour variants',
    })],
    [PACKAGE_UPGRADE_VALUES.PLATINUM, intl.formatMessage({
      id: '7FPVBz9Ex7TA4dhDwHSx',
      defaultMessage: 'Deluxe to Regency Suite w. Club Access Upgrade',
      description: 'Package upgrade label for the platinum tour package option on room-range tour variants',
    })],
  ])

  switch (offerId?.replace('tour-', '')) {
    case 'bbf97caa-d857-4517-b768-465f5ceac8db':
    case '15275af7-48cb-4a5e-ad0c-eb9b3efc1619':
    case '6f77e732-d5e1-4747-ae47-901022e36fc3':
      return ecoBusinessPackageUpgradeDisplayTextMap
    case '6909c91b-cc92-4c0b-86d4-d663263436be':
      return viewPackageUpgradeDisplayTextMap
    case '1ea35824-8354-4656-9d76-c24d717ee700':
      return rangeViewPackageUpgradeDisplayTextMap
    default:
      return defaultPackageUpgradeDisplayTextMap
  }
}

export enum TOURS_GROUP_TYPE {
  isPrivateRequest = 'isPrivateRequest',
  isSmallGroup = 'isSmallGroup',
  isLargeGroup = 'isLargeGroup',
  isFamilyFriendly = 'isFamilyFriendly',
}

export const TOURS_GROUP_TYPES_LIST = Object.values(TOURS_GROUP_TYPE)

export enum TOUR_OFFER_TAG {
  onSale = 'onSale',
  flashOffer = 'flashOffer',
}

export const TOUR_OFFER_TAG_LIST = Object.values(TOUR_OFFER_TAG)

export const TOUR_MAX_ROOM_LIMIT = 5

export const TOUR_OPTIONAL_EXPERIENCE_BOOKING_DAYS_BEFORE_DEPARTURE = 60

export const TOUR_OPTIONAL_EXPERIENCE_RESERVATION_STATUS_CONFIRMED = 'confirmed'
export const TOUR_OPTIONAL_EXPERIENCE_ITEM_STATUS_COMPLETED = 'completed'

export const BRAND_SIGNATURE_SERIES = 'lesignaturetours'

export const PROMO_CODE_MAX_DISCOUNT_REGION: Record<string, string> = {
  AU: formatCurrency(1000, 'AUD', 'AU', { format: 'dynamic' }),
  NZ: formatCurrency(1000, 'NZD', 'NZ', { format: 'dynamic' }),
  SG: formatCurrency(1000, 'SGD', 'SG', { format: 'dynamic' }),
  US: formatCurrency(600, 'USD', 'US', { format: 'dynamic' }),
  GB: formatCurrency(500, 'GBP', 'GB', { format: 'dynamic' }),
  HK: formatCurrency(5000, 'HKD', 'HK', { format: 'dynamic' }),
}

export const PROMO_SPEND_EARN_REGION: Record<string, Array<{ spend: number, earn: number }>> = {
  AU: [{ spend: 4999, earn: 250 }, { spend: 6999, earn: 300 }, { spend: 9999, earn: 500 }, { spend: 19999, earn: 750 }],
  NZ: [{ spend: 5499, earn: 250 }, { spend: 7499, earn: 300 }, { spend: 10499, earn: 500 }, { spend: 15999, earn: 750 }],
  SG: [{ spend: 4499, earn: 250 }, { spend: 6499, earn: 300 }, { spend: 9499, earn: 500 }, { spend: 13999, earn: 750 }],
  HK: [{ spend: 24999, earn: 1250 }, { spend: 33999, earn: 1500 }, { spend: 49999, earn: 2500 }, { spend: 74999, earn: 3750 }],
  GB: [{ spend: 2499, earn: 120 }, { spend: 3499, earn: 149 }, { spend: 4999, earn: 249 }, { spend: 9999, earn: 349 }],
  US: [{ spend: 2299, earn: 150 }, { spend: 4499, earn: 200 }, { spend: 6499, earn: 300 }, { spend: 12999, earn: 500 }],
  CA: [{ spend: 2299, earn: 150 }, { spend: 4499, earn: 200 }, { spend: 6499, earn: 300 }, { spend: 12999, earn: 500 }],
  FR: [{ spend: 2499, earn: 120 }, { spend: 3499, earn: 169 }, { spend: 4999, earn: 249 }, { spend: 9999, earn: 349 }],
  IT: [{ spend: 2499, earn: 120 }, { spend: 3499, earn: 169 }, { spend: 4999, earn: 249 }, { spend: 9999, earn: 349 }],
  ES: [{ spend: 2499, earn: 120 }, { spend: 3499, earn: 169 }, { spend: 4999, earn: 249 }, { spend: 9999, earn: 349 }],
  NL: [{ spend: 2499, earn: 120 }, { spend: 3499, earn: 169 }, { spend: 4999, earn: 249 }, { spend: 9999, earn: 349 }],
  DE: [{ spend: 2499, earn: 120 }, { spend: 3499, earn: 169 }, { spend: 4999, earn: 249 }, { spend: 9999, earn: 349 }],
  IE: [{ spend: 2499, earn: 120 }, { spend: 3499, earn: 169 }, { spend: 4999, earn: 249 }, { spend: 9999, earn: 349 }],
}

export const EXCLUDE_OFFER_PROMO_SPEND_EARN = ['tour-b9fcebec-2e68-42d5-a5b5-b09c69930d87']
export const END_SPEND_EARN_PROMO_TAG_DATE = new Date('2025-03-10')

export const TOUR_ASIA_TAKEOVER_SEARCH_PLACE_IDS = [
  Places.India.id,
  Places.SriLanka.id,
  Places.Thailand.id,
  Places.Vietnam.id,
  Places.Japan.id,
  Places.China.id,
  Places.Malaysia.id,
  Places.Nepal.id,
]
export const TOUR_ASIA_TAKEOVER_SHOW_SEARCH_BANNER_PLACE_IDS = [
  ...TOUR_ASIA_TAKEOVER_SEARCH_PLACE_IDS,
  Places.Anywhere.id,
  Places.Asia.id,
  Places.SouthEastAsia.id,
]

export const TOUR_HERO_IMAGES_IDS_ROTATION: Array<{ id: string, gravity: 'north' | 'center' }> = [
  { id: 'bxwkiui0zplo7rexp1sp', gravity: 'center' },
  { id: 'buxxor3j5kmy5vqo9klc', gravity: 'center' },
  { id: 'byfoq3zduoearfn090g', gravity: 'north' },
  { id: 'l0clydv7291tl39ud6', gravity: 'center' },
  { id: 'wp4rzuxk8fzq899bpd', gravity: 'center' },
]

export const TOUR_DEFAULT_DEAL_HERO_IMAGE_ID = 'b1c309a3-eb76-4e00-8748-bdf59fd7d714'
export const TOUR_DEFAULT_DEAL_HIGHLIGHT_COLOUR = '#232B5C'

export const TOUR_ASIA_PROMO_REGIONS: Record<string, number> = {
  AU: 849,
  NZ: 920,
  US: 520,
  GB: 430,
  SG: 710,
  HK: 4090,
  CA: 760,
  FR: 490,
  IT: 490,
  ES: 490,
  NL: 490,
  DE: 490,
  IE: 490,
}
